import { MatterType, MatterTypeConfig, Matter } from '../types';

// Matter Type Configurations
export const MATTER_TYPE_CONFIGS: Record<MatterType, MatterTypeConfig> = {
  'Witness Prep': {
    type: 'Witness Prep',
    color: '#FF9800', // Orange
    borderColor: 'border-orange-500',
    icon: 'Person',
    description: 'Witness preparation and coaching sessions',
  },
  'Focus Group': {
    type: 'Focus Group',
    color: '#2196F3', // Blue
    borderColor: 'border-blue-500',
    icon: 'Group',
    description: 'Focus group research and analysis',
  },
  'Jury Selection': {
    type: 'Jury Selection',
    color: '#4CAF50', // Green
    borderColor: 'border-green-500',
    icon: 'Gavel',
    description: 'Jury selection consulting and voir dire',
  },
  'Jury Confirm': {
    type: 'Jury Confirm',
    color: '#8BC34A', // Light Green
    borderColor: 'border-green-400',
    icon: 'CheckCircle',
    description: 'Jury confirmation and validation',
  },
  'Shadow Jury': {
    type: 'Shadow Jury',
    color: '#9C27B0', // Purple
    borderColor: 'border-purple-500',
    icon: 'Visibility',
    description: 'Shadow jury observation and analysis',
  },
  'Jury Scout': {
    type: 'Jury Scout',
    color: '#795548', // Brown
    borderColor: 'border-yellow-600',
    icon: 'Search',
    description: 'Jury research and background investigation',
  },
  'Cogent Prep': {
    type: 'Cogent Prep',
    color: '#607D8B', // Blue Grey
    borderColor: 'border-gray-500',
    icon: 'School',
    description: 'Cogent preparation and training',
  },
  'Research Pending': {
    type: 'Research Pending',
    color: '#607D8B', // Blue Grey
    borderColor: 'border-gray-500',
    icon: 'Search',
    description: 'Legal research and analysis',
  },
  Other: {
    type: 'Other',
    color: '#9E9E9E', // Grey
    borderColor: 'border-gray-500',
    icon: 'Help',
    description: 'Other matter types',
  },
};

// Calendar View Options
export const CALENDAR_VIEWS = {
  MONTH: 'month',
  WEEK: 'week',
  DAY: 'day',
} as const;

// Date Formats
export const DATE_FORMATS = {
  DISPLAY: 'MMM d, yyyy',
  API: 'yyyy-MM-dd',
  MONTH_YEAR: 'MMMM yyyy',
  SHORT_DATE: 'MMM d',
  FULL_DATE: 'EEEE, MMMM d, yyyy',
  TIME: 'h:mm a',
  DATETIME: 'MMM d, yyyy h:mm a',
} as const;

// BigTime Deep Link Configuration
export const BIGTIME_LINKS = {
  BASE_URL: 'https://iq.bigtime.net/Bigtime/Project2/soloview#/detail',
  GENERATE_URL: (projectId: string) => `${BIGTIME_LINKS.BASE_URL}/${projectId}`,
} as const;

// Application Settings
export const APP_SETTINGS = {
  ITEMS_PER_PAGE: 25,
  MAX_EVENTS_PER_DAY: 3,
  AUTO_REFRESH_INTERVAL: 300000, // 5 minutes
  CACHE_DURATION: 900000, // 15 minutes
  DEFAULT_TIME_ZONE: 'America/New_York',
} as const;

// Filter Options
export const FILTER_OPTIONS = {
  ALL: 'All',
  DATE_RANGES: [
    { label: 'Next 7 days', days: 7 },
    { label: 'Next 30 days', days: 30 },
    { label: 'Next 90 days', days: 90 },
    { label: 'Next 6 months', days: 180 },
  ],
} as const;

// Date Type Colors (colorblind-accessible)
export const DATE_TYPE_COLORS = {
  DUE: {
    type: 'due',
    color: '#E9594E', // Vermilion for due dates
    backgroundColor: '#FDEAE8',
    label: 'Due Date',
  },
  SPAN: {
    type: 'span',
    color: '#193CBA', // Persian Blue for span dates
    backgroundColor: '#E8EDFD',
    label: 'Span Date',
  },
  ADDITIONAL: {
    type: 'additional',
    color: '#EDE65A', // Maize for additional dates
    backgroundColor: '#FDFCE8',
    label: 'Additional Date',
  },
  TRIAL: {
    type: 'trial',
    color: '#193CBA', // Persian Blue for trial dates (same as span)
    backgroundColor: '#E8EDFD',
    label: 'Trial Date',
  },
} as const;

// Theme Colors
export const THEME_COLORS = {
  PRIMARY: '#1976d2',
  SECONDARY: '#dc004e',
  SUCCESS: '#4caf50',
  ERROR: '#f44336',
  WARNING: '#ff9800',
  INFO: '#2196f3',
  BACKGROUND: '#f5f5f5',
  SURFACE: '#ffffff',
  TEXT_PRIMARY: '#212121',
  TEXT_SECONDARY: '#757575',
} as const;

// Breakpoints (matching Material-UI)
export const BREAKPOINTS = {
  XS: 0,
  SM: 600,
  MD: 900,
  LG: 1200,
  XL: 1536,
} as const;

// Error Messages
export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Unable to connect to BigTime. Please check your internet connection.',
  AUTH_ERROR: 'Authentication failed. Please check your API credentials.',
  RATE_LIMIT: 'Too many requests. Please wait a moment and try again.',
  NOT_FOUND: 'The requested matter could not be found.',
  SERVER_ERROR: 'A server error occurred. Please try again later.',
  INVALID_DATE: 'Invalid date format. Please use MM/DD/YYYY format.',
  LOAD_ERROR: 'Failed to load calendar data. Please refresh the page.',
  UNKNOWN_ERROR: 'An unexpected error occurred. Please try again.',
} as const;

// Success Messages
export const SUCCESS_MESSAGES = {
  DATA_LOADED: 'Calendar data loaded successfully.',
  NOTES_UPDATED: 'Matter notes updated successfully.',
  FILTER_APPLIED: 'Filter applied successfully.',
  EXPORT_COMPLETE: 'Calendar data exported successfully.',
} as const;

// Local Storage Keys
export const STORAGE_KEYS = {
  API_TOKEN: 'bigtime_api_token',
  USER_PREFERENCES: 'calendar_preferences',
  FILTER_STATE: 'calendar_filters',
  CACHE_PREFIX: 'bt_cache_',
  LAST_REFRESH: 'last_refresh',
} as const;

// CSV Export Headers
export const CSV_HEADERS = [
  'Matter ID',
  'Client',
  'Matter Name',
  'Case Location',
  'Type',
  'Lead Consultant',
  'Secondary Consultant',
  'Technician',
  'Due Date',
  'Trial Presentation',
  'Deliberation Groups',
  'Notes',
  'Status',
] as const;

// Regular Expressions
export const REGEX_PATTERNS = {
  MATTER_ID: /^[A-Z]{2,4}-\d{4,6}$/,
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  PHONE: /^\(\d{3}\)\s\d{3}-\d{4}$/,
  DATE_MM_DD_YYYY: /^\d{1,2}\/\d{1,2}\/\d{4}$/,
  DATE_MM_DD_YY: /^\d{1,2}\/\d{1,2}\/\d{2}$/,
} as const;

// Feature Flags
export const FEATURE_FLAGS = {
  ENABLE_REAL_TIME_UPDATES: process.env.REACT_APP_ENABLE_REAL_TIME === 'true',
  ENABLE_EXPORT: process.env.REACT_APP_ENABLE_EXPORT !== 'false',
  ENABLE_MOBILE_VIEW: true,
  ENABLE_DARK_MODE: process.env.REACT_APP_ENABLE_DARK_MODE === 'true',
  ENABLE_NOTIFICATIONS: process.env.REACT_APP_ENABLE_NOTIFICATIONS === 'true',
} as const;


export default {
  MATTER_TYPE_CONFIGS,
  CALENDAR_VIEWS,
  DATE_FORMATS,
  BIGTIME_LINKS,
  APP_SETTINGS,
  FILTER_OPTIONS,
  DATE_TYPE_COLORS,
  THEME_COLORS,
  BREAKPOINTS,
  ERROR_MESSAGES,
  SUCCESS_MESSAGES,
  STORAGE_KEYS,
  CSV_HEADERS,
  REGEX_PATTERNS,
  FEATURE_FLAGS,
};
